<script setup>
import { ref, onMounted, onUnmounted, defineProps, defineEmits } from 'vue'
import Dialog from 'primevue/dialog'
import Button from 'primevue/button'
import InputText from 'primevue/inputtext'
import { useSalidaTokens } from '@/composables/useSalidaTokens'
import { useRouterStore } from '@/store/router'

const props = defineProps({
  visible: Boolean,
  salidas: { type: Array, default: () => [] }, // lista de { id, label } para poder asociar
  preselectSalidaIds: { type: Array, default: () => [] },
})
const emit = defineEmits(['update:visible'])

const { getPublicUrl, getTokens, createToken, revokeToken, addSalidaToToken, removeSalidaFromToken, getAdminLocations } =
  useSalidaTokens()
const router = useRouterStore()

const tokens = ref([])
const newTokenName = ref('')
const loading = ref(false)
const copiedToken = ref(null)
const locationsMap = ref({})   // token -> [{ userName, lat, lng, grupos, ts }]
const locationsLoading = ref({})
let locationPolls = {}

const loadLocations = async (token) => {
  locationsLoading.value = { ...locationsLoading.value, [token]: true }
  const res = await getAdminLocations(token)
  locationsMap.value = { ...locationsMap.value, [token]: res.locations || [] }
  locationsLoading.value = { ...locationsLoading.value, [token]: false }
}

const toggleLocations = (token) => {
  if (locationPolls[token]) {
    clearInterval(locationPolls[token])
    delete locationPolls[token]
    const m = { ...locationsMap.value }
    delete m[token]
    locationsMap.value = m
    return
  }
  loadLocations(token)
  locationPolls[token] = setInterval(() => loadLocations(token), 10000)
}

const formatAgo = (ts) => {
  const s = Math.floor((Date.now() - ts) / 1000)
  if (s < 60) return `hace ${s}s`
  return `hace ${Math.floor(s / 60)}min`
}

onUnmounted(() => {
  Object.values(locationPolls).forEach(clearInterval)
})

const load = async () => {
  loading.value = true
  const res = await getTokens()
  tokens.value = res.tokens || []
  loading.value = false
}

onMounted(load)

const handleCreate = async () => {
  if (!newTokenName.value.trim()) return
  loading.value = true
  const created = await createToken(newTokenName.value.trim())
  if (created.token) {
    if (props.preselectSalidaIds.length) {
      for (const salidaId of props.preselectSalidaIds) {
        await addSalidaToToken(created.token, salidaId)
      }
      created.salidaIds = [...props.preselectSalidaIds]
    }
    tokens.value.unshift(created)
    copyUrl(created.token)
  }
  newTokenName.value = ''
  loading.value = false
}

const handleRevoke = async (token) => {
  await revokeToken(token)
  tokens.value = tokens.value.filter((t) => t.token !== token)
}

const handleAddSalida = async (token, salidaId) => {
  const res = await addSalidaToToken(token, salidaId)
  const t = tokens.value.find((t) => t.token === token)
  if (t && res.salidaIds) t.salidaIds = res.salidaIds
}

const handleRemoveSalida = async (token, salidaId) => {
  const res = await removeSalidaFromToken(token, salidaId)
  const t = tokens.value.find((t) => t.token === token)
  if (t && res.salidaIds) t.salidaIds = res.salidaIds
}

const copyUrl = (token) => {
  navigator.clipboard.writeText(getPublicUrl(token))
  copiedToken.value = token
  setTimeout(() => { copiedToken.value = null }, 2000)
}

const getSalidaLabel = (id) => {
  const found = props.salidas.find((s) => s.id === id || String(s.id) === String(id))
  return found ? found.label : id
}

const availableSalidas = (tokenSalidaIds) =>
  props.salidas.filter((s) => !tokenSalidaIds.includes(String(s.id)) && !tokenSalidaIds.includes(s.id))
</script>

<template>
  <Dialog
    :visible="props.visible"
    @update:visible="emit('update:visible', $event)"
    modal
    header="Gestionar enlaces de acceso"
    :style="{ width: '560px' }"
  >
    <!-- Crear nuevo token -->
    <div class="flex gap-2 mb-1">
      <InputText
        v-model="newTokenName"
        placeholder="Nombre del enlace (ej: Grupo 1)"
        class="flex-1"
        @keyup.enter="handleCreate"
      />
      <Button label="Crear enlace" :disabled="!newTokenName.trim() || loading" @click="handleCreate" />
    </div>
    <div v-if="props.preselectSalidaIds.length" class="text-xs text-500 mb-3">
      Se asociarán automáticamente {{ props.preselectSalidaIds.length }}
      {{ props.preselectSalidaIds.length === 1 ? 'salida' : 'salidas' }} al crear el enlace.
    </div>
    <div v-else class="mb-3"></div>

    <div v-if="loading" class="text-center py-3 text-500">Cargando...</div>

    <div v-else-if="tokens.length === 0" class="text-center py-3 text-500">
      No hay enlaces creados. Crea uno arriba.
    </div>

    <div v-else class="flex flex-column gap-3">
      <div
        v-for="t in tokens"
        :key="t.token"
        class="token-card"
      >
        <!-- Cabecera del token -->
        <div class="flex justify-content-between align-items-center mb-2">
          <span class="font-semibold">{{ t.name }}</span>
          <div class="flex gap-2">
            <Button
              :icon="copiedToken === t.token ? 'pi pi-check' : 'pi pi-link'"
              :label="copiedToken === t.token ? 'Copiado' : 'Copiar enlace'"
              size="small"
              outlined
              @click="copyUrl(t.token)"
            />
            <Button
              icon="pi pi-trash"
              size="small"
              severity="danger"
              outlined
              @click="handleRevoke(t.token)"
            />
          </div>
        </div>

        <!-- Salidas incluidas -->
        <div class="text-xs text-500 mb-1">Salidas incluidas:</div>
        <div class="flex flex-wrap gap-1 mb-2">
          <div
            v-for="sid in t.salidaIds"
            :key="sid"
            class="token-chip flex align-items-center gap-1 text-xs"
          >
            <span>{{ getSalidaLabel(sid) }}</span>
            <Button
              icon="pi pi-times"
              text
              size="small"
              class="p-0"
              style="width: 1rem; height: 1rem"
              @click="handleRemoveSalida(t.token, sid)"
            />
          </div>
          <span v-if="t.salidaIds.length === 0" class="text-xs text-400">Sin salidas</span>
        </div>

        <!-- Agregar salida -->
        <div v-if="props.salidas.length > 0 && availableSalidas(t.salidaIds).length > 0" class="flex gap-2 flex-wrap">
          <Button
            v-for="s in availableSalidas(t.salidaIds)"
            :key="s.id"
            :label="`+ ${s.label}`"
            size="small"
            text
            severity="success"
            class="p-0 text-xs"
            @click="handleAddSalida(t.token, String(s.id))"
          />
        </div>

        <!-- Ubicaciones en vivo -->
        <div class="mt-2 pt-2" style="border-top: 1px solid var(--separator)">
          <div class="flex gap-2 align-items-center">
            <Button
              :label="locationsMap[t.token] !== undefined ? 'Ocultar' : 'Ver activos'"
              :icon="locationsMap[t.token] !== undefined ? 'pi pi-eye-slash' : 'pi pi-wifi'"
              size="small"
              text
              :loading="locationsLoading[t.token]"
              @click="toggleLocations(t.token)"
            />
            <Button
              label="Ver mapa"
              icon="pi pi-map"
              size="small"
              text
              severity="success"
              @click="() => { emit('update:visible', false); router.page = 'monitorSalida'; router.params = { token: t.token, name: t.name } }"
            />
          </div>
          <div v-if="locationsMap[t.token] !== undefined" class="mt-2">
            <div v-if="!locationsMap[t.token]?.length" class="text-xs text-500">
              Sin usuarios compartiendo posición ahora
            </div>
            <div
              v-for="loc in locationsMap[t.token]"
              :key="loc.userName"
              class="flex align-items-center gap-2 py-1 text-xs"
            >
              <div class="w-2rem h-2rem border-round-xl flex align-items-center justify-content-center font-bold text-white text-sm flex-shrink-0"
                style="background:var(--accent)">
                {{ loc.userName.charAt(0).toUpperCase() }}
              </div>
              <div class="flex-1">
                <div class="font-medium">{{ loc.userName }}</div>
                <div class="text-400">{{ formatAgo(loc.ts) }}</div>
              </div>
              <a
                :href="`https://www.google.com/maps?q=${loc.lat},${loc.lng}`"
                target="_blank"
                class="text-primary no-underline"
                title="Ver en Google Maps"
              >
                <i class="pi pi-map-marker" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Dialog>
</template>

<style scoped>
.token-card {
  background: var(--bg-secondary);
  border: 1px solid var(--separator);
  border-radius: var(--radius-md);
  padding: var(--space-3);
}
.token-chip {
  background: var(--fill-quaternary);
  border: 1px solid var(--separator);
  border-radius: var(--radius-pill);
  padding: var(--space-1) var(--space-2);
}
</style>
